<template>
  <Modal
    name="EDIT_VISITOR_MODAL"
    :title="modalTitle"
    ref="rescheduleAppointmentModal"
    actions
  >
    <template v-slot:body>
      <v-progress-linear
        v-if="isAppointmentLoading"
        class="mt-5 mb-7"
        indeterminate
        rounded
      />

      <v-form
        v-show="!isAppointmentLoading"
        ref="visitorForm"
      >

        <v-text-field
          v-model="name"
          :label="$t('appointment.formCreate.labels.visitorName')"
          :rules="isRentOutOrVisit ? [rules.name] : [rules.nameCheckPassEmpty]"
          hide-details="auto"
          class="mb-5"
          outlined
        />

        <v-text-field
          v-model="email"
          :label="$t('appointment.formCreate.labels.visitorEmail')"
          :rules="isVisit ? [rules.email] : [rules.emailCheckPassEmpty]"
          hide-details="auto"
          class="mb-5"
          outlined
        />

        <HPhone
          v-model="phone"
          :country="country"
          :default-value="phone"
          :rules="isRentOutOrVisit ? [rules.phone] : [rules.phoneCheckPassEmpty]"
        />

        <v-row>
          <v-col class="pb-0 mb-0" cols="12" sm="7">
            <HDocumentID
              v-model="document"
              :country="country"
              :default-value="document"
              :rules="isRentOut ? [rules.documentCheck] : [rules.documentCheckPassEmpty]"
              :isRentOut="isRentOut"
            />
          </v-col>
          <v-col class="pb-0 mb-0" cols="12" sm="5">
            <v-text-field
              v-model="birthDate"
              v-mask="'##/##/####'"
              :label="$t('appointment.formCreate.labels.visitorBirthDate')"
              :hint="$t('forms.dateHint')"
              :rules="isRentOut ? [rules.birthDateCheck] : [rules.birthDateCheckPassEmpty]"
              hide-details="auto"
              persistent-hint
              outlined
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="closeAllModals"
        elevation="0"
        class="mr-3"
        color="grey"
        large
        text
      >
        {{ $t('appointment.visitor.cancel') }}
      </v-btn>

      <v-btn
        @click="validate"
        :loading="isVisitorUpdating"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('appointment.visitor.update') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import Modal from '@/components/app/Modal'
import HDocumentID from '../../app/HDocumentID'
import HPhone from '../../app/HPhone'

import {
  name,
  email,
  phone,
  documentCheck,
  birthDateCheck,
  nameCheckPassEmpty,
  emailCheckPassEmpty,
  phoneCheckPassEmpty,
  documentCheckPassEmpty,
  birthDateCheckPassEmpty,
} from '@/util/errorHandling'
import { rentOutTypes, visitTypeServices } from '@/components/appointment/formFieldsCreate/appointmentFormDefaults'

export default {
  name: 'ModalAppointmentVisitorEdit',

  components: {
    Modal,
    HDocumentID,
    HPhone,
  },

  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    }
  },

  computed: {
    ...mapGetters([
      'appointment',
      'isVisitorUpdating',
      'isAppointmentLoading',
    ]),

    isRentOut() {
      return rentOutTypes.includes(this.appointment.type)
    },

    isVisit() {
      return visitTypeServices.includes(this.appointment?.type)
    },

    isRentOutOrVisit() {
      return this.isVisit || this.isRentOut
    },

    modalTitle() {
      return this.isRentOut
        ? this.$t('appointment.visitor.updateTenant')
        : this.$t('appointment.visitor.updateVisitor')
    },

    country() {
      return localStorage.getItem('user_country')
    },
  },

  watch: {
    appointment(appointment) {
      if (!appointment) return

      this.name = appointment.visitor_name
      this.email = appointment.visitor_mail
      this.phone = appointment.visitor_phone
      this.document = appointment.visitor_document
      this.birthDate = this.maskDate(appointment.visitor_birth_date, 'br')
    }
  },

  methods: {
    ...mapActions(['updateVisitorData']),
    ...mapMutations(['closeAllModals']),

    validate() {
      if (!this.$refs.visitorForm.validate()) return

      const visitorData = {
        appointment_id: this.appointment.id,
        visitor_name: this.name,
        visitor_mail: this.email,
        visitor_phone: this.phone,
        visitor_document: this.document ? this.clearValue(this.document) : null,
        visitor_birth_date: this.birthDate ? this.formatBirthDate(this.birthDate) : null
      }

      this.updateVisitorData({ visitorData, isAdmin: this.isAdmin })
        .then(() => {
          if (this.$parent?.refreshMap) this.$parent?.refreshMap()
          if (this.$parent?.refreshAppointments) this.$parent?.refreshAppointments()
        })
        .then(() => this.closeAllModals())
    },

    maskDate(date, type) {
      if (!date) return

      const parsedDate = this.clearValue(date)

      return type === 'br'
        ? parsedDate.replace(/^(\d{4})(\d{2})(\d{2}).*/, '$3/$2/$1')
        : parsedDate.replace(/^(\d{2})(\d{2})(\d{4}).*/, '$3-$2-$1')
    },

    formatBirthDate(date) {
      if (!date) return
      return this.maskDate(date) + 'T12:00:00-03:00'
    },

    clearValue(value) {
      return value.replace(/[^\d]+/g, '')
    },
  },

  data() {
    return {
      name: '',
      date: '',
      email: '',
      phone: '',
      document: '',
      birthDate: '',
      completeDate: '',

      rules: {
        name,
        email,
        phone,
        documentCheck,
        birthDateCheck,
        nameCheckPassEmpty,
        emailCheckPassEmpty,
        phoneCheckPassEmpty,
        documentCheckPassEmpty,
        birthDateCheckPassEmpty,
      }
    }
  }
}
</script>
